import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const HERO_IMG =
  "https://images.unsplash.com/photo-1733935156769-fe0c2a600d6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdvbGQlMjB0ZXh0dXJlJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzkyNTUzNDY1fDA&ixlib=rb-4.1.0&q=85";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.16 },
  }),
};

const MaskedLine = ({ children, index, className = "" }) => (
  <span className={`block overflow-hidden pb-1 ${className}`}>
    <motion.span
      className="block"
      custom={index}
      variants={lineVariants}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src={HERO_IMG} alt="" className="w-full h-[130%] object-cover opacity-25" />
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-x-0 bottom-0 h-64 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="max-w-7xl mx-auto px-6 lg:px-12 pt-32 pb-24 w-full">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.8 }}
          className="inline-flex items-center gap-3 border border-[#D4AF37]/40 px-4 py-2 mb-10"
          data-testid="hero-prelaunch-badge"
        >
          <span className="h-1.5 w-1.5 rounded-full bg-[#D4AF37] animate-pulse" />
          <span className="text-[0.65rem] tracking-[0.35em] uppercase text-[#D4AF37]">Currently in Pre-Launch</span>
        </motion.div>

        <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-8xl leading-[0.95] tracking-tight text-[#F5F5F5] max-w-5xl" data-testid="hero-heading">
          <MaskedLine index={0}>Executive education</MaskedLine>
          <MaskedLine index={1}>
            for women <em className="text-[#D4AF37] not-italic font-medium">stepping into</em>
          </MaskedLine>
          <MaskedLine index={2}>
            <em className="font-light italic text-[#D4AF37]">the boardroom.</em>
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.15, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 max-w-xl text-base sm:text-lg leading-relaxed text-white"
          data-testid="hero-subheading"
        >
          SHE Global™ Institute is being built for experienced women in transition — toward leadership,
          entrepreneurship, and governance. Its flagship program, the SHE Global™ Boardroom Accelerator,
          is designed around strategic decision-making, financial understanding, and governance readiness.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mt-12 flex flex-wrap items-center gap-5"
        >
          <button
            onClick={() => document.querySelector("#interest")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-express-interest-btn"
            className="bg-[#D4AF37] text-black text-xs tracking-[0.3em] uppercase font-semibold px-10 py-5 hover:bg-[#C5A24D] transition-colors duration-300"
          >
            Express Interest
          </button>
          <button
            onClick={() => document.querySelector("#about")?.scrollIntoView({ behavior: "smooth" })}
            data-testid="hero-discover-btn"
            className="border border-white/25 text-[#F5F5F5] text-xs tracking-[0.3em] uppercase px-10 py-5 hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
          >
            Discover the Institute
          </button>
        </motion.div>
      </motion.div>

    </section>
  );
};
